import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Loader2, Scan, Check, X, Tag, Trash2, Plus, Nfc, AlertCircle } from "lucide-react";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/ToastContext";

const NFCTaggingInterface = ({ donation, onUpdate, showHeader = true }) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentTags, setCurrentTags] = useState([]); // Tags currently being scanned in the session
    const [inputVal, setInputVal] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef(null);
    const { addToast } = useToast();

    // Reset state when dialog opens for a new item
    const handleOpenScan = (item) => {
        setSelectedItem(item);
        // If the item already has tags on the backend, we might want to show them?
        // backend structure: item.nfc_ids = [...]
        setCurrentTags(item.nfc_ids || []);
        setIsDialogOpen(true);
    };

    const handleClose = () => {
        setIsDialogOpen(false);
        setSelectedItem(null);
        setInputVal("");
        setCurrentTags([]);
    };

    // Handle "Scan" (Enter key on input)
    const handleScan = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTag();
        }
    };

    const addTag = () => {
        processTag(inputVal.trim());
    };

    const removeTag = (tagToRemove) => {
        setCurrentTags(currentTags.filter(t => t !== tagToRemove));
    };

    const handleSave = async () => {
        if (!selectedItem) return;
        setIsSaving(true);
        try {
            // endpoint: POST /api/admin/donations/:id/items/:itemId/nfc
            // body: { nfc_ids: [...] }
            await apiClient.post(`/admin/donations/${donation._id}/items/${selectedItem._id}/nfc`, {
                nfc_ids: currentTags
            });

            addToast("NFC Tags saved successfully", "success");
            onUpdate(); // Trigger refresh in parent
            handleClose();

        } catch (error) {
            console.error(error);
            addToast(error.response?.data?.msg || "Failed to save tags", "error");
        } finally {
            setIsSaving(false);
        }
    };

    // Web NFC API Support
    const [nfcError, setNfcError] = useState(null);

    const startNfcScan = async () => {
        if (!('NDEFReader' in window)) {
            addToast("NFC not supported on this device/browser. Use Chrome on Android.", "error");
            return;
        }
        try {
            // eslint-disable-next-line no-undef
            const ndef = new window.NDEFReader();
            await ndef.scan();
            addToast("Ready to scan. Tap an NFC tag.", "info");

            ndef.addEventListener("reading", ({ message, serialNumber }) => {
                const tagId = serialNumber ? serialNumber.toUpperCase().replace(/:/g, '') : "UNKNOWN_ID";
                addToast(`Tag detected: ${tagId}`, "success");

                // Directly add the tag
                handleAddTagDirect(tagId);
            });
        } catch (error) {
            console.error(error);
            setNfcError(error.message);
            addToast(`NFC Error: ${error.message}`, "error");
        }
    };

    const handleAddTagDirect = (val) => {
        // Create a synthetic event or just call logic directly
        // We need to access the LATEST state of currentTags, but standard closure might be stale.
        // Best to use functional state update in addTag logic?
        // Refactoring addTag to be reusable or using inputVal state for manual entry.
        setInputVal(val);
        // slightly hacky but effective: update input then trigger add
        // Check "addTag" implementation below. 
        // It uses `inputVal` state. State updates are async.
        // Better to modify addTag to accept an argument.
        processTag(val);
    };

    const processTag = (val) => {
        if (!val) return;

        // We need to use functional updates or refs for currentTags if this is called from an event listener
        setCurrentTags(prevTags => {
            if (prevTags.includes(val)) {
                addToast("Tag already in list!", "error");
                return prevTags;
            }
            if (prevTags.length >= selectedItem?.quantity) {
                addToast(`Max quantity reached!`, "warning");
                return prevTags;
            }
            addToast("Tag added", "success");
            return [...prevTags, val];
        });
        setInputVal(""); // Clear input if it was used
    };

    // Auto-focus input
    useEffect(() => {
        if (isDialogOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 100);
        }
    }, [isDialogOpen]);

    return (
        <div className="space-y-4">
            {showHeader && (
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Tag className="w-5 h-5 text-blue-500" /> NFC Tagging
                </h3>
            )}

            <div className="grid gap-3">
                {donation.items.map((item) => {
                    const assignedCount = item.nfc_ids?.length || 0;
                    const isComplete = assignedCount >= item.quantity;

                    return (
                        <div key={item._id} className="flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className={`p-2 rounded-full ${isComplete ? 'bg-green-100 text-green-600' : 'bg-zinc-100 text-zinc-500'}`}>
                                    {isComplete ? <Check className="w-5 h-5" /> : <Tag className="w-5 h-5" />}
                                </div>
                                <div>
                                    <p className="font-medium text-zinc-900 dark:text-zinc-100">{item.name}</p>
                                    <p className="text-sm text-zinc-500">{item.category} • Qty: {item.quantity}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <Badge variant={isComplete ? "success" : "secondary"} className={isComplete ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-100"}>
                                    {assignedCount} / {item.quantity} Tagged
                                </Badge>

                                <Button
                                    size="sm"
                                    variant={isComplete ? "outline" : "default"}
                                    onClick={() => handleOpenScan(item)}
                                >
                                    <Scan className="w-4 h-4 mr-2" />
                                    {isComplete ? "Manage Tags" : "Scan Tags"}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Scan NFC Tags</DialogTitle>
                        <DialogDescription>
                            Scanning for <strong>{selectedItem?.name}</strong>. Target: <strong>{selectedItem?.quantity}</strong> tags.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        {(!('NDEFReader' in window)) && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm mb-2 border border-red-200">
                                <p className="font-semibold flex items-center gap-1"><AlertCircle className="w-4 h-4" /> NFC Not Available</p>
                                <p>Use Chrome on Android with HTTPS.</p>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Input
                                ref={inputRef}
                                value={inputVal}
                                onChange={(e) => setInputVal(e.target.value)}
                                onKeyDown={handleScan}
                                placeholder="Scan NFC tag here..."
                                className="font-mono text-center text-lg tracking-wider"
                                disabled={currentTags.length >= selectedItem?.quantity}
                            />
                            <Button onClick={addTag} disabled={!inputVal || currentTags.length >= selectedItem?.quantity} size="icon">
                                <Plus className="w-5 h-5" />
                            </Button>
                            <Button onClick={startNfcScan} variant="outline" size="icon" title="Start NFC Reader">
                                <Nfc className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Progress Bar */}
                        <div className="h-2 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-300 ${currentTags.length >= selectedItem?.quantity ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${(currentTags.length / (selectedItem?.quantity || 1)) * 100}%` }}
                            />
                        </div>
                        <p className="text-center text-sm text-zinc-500">
                            {currentTags.length} of {selectedItem?.quantity} scanned
                        </p>

                        {/* Tag List */}
                        <div className="max-h-[200px] overflow-y-auto space-y-2 border border-zinc-200 dark:border-zinc-800 rounded-lg p-2 bg-zinc-50 dark:bg-zinc-900/50">
                            {currentTags.length === 0 ? (
                                <div className="text-center py-6 text-zinc-400 text-sm">No tags scanned yet</div>
                            ) : (
                                currentTags.map((tag, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded shadow-sm animate-in fade-in slide-in-from-top-1">
                                        <span className="font-mono text-sm">{tag}</span>
                                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeTag(tag)}>
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
                            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Save Tags
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NFCTaggingInterface;
